/**
 * Failure card shown in place of a surface that threw while rendering.
 *
 * One presentational component serves all three render-error boundaries
 * (REQ-UI-191): the per-panel boundary in the dockview registry, the
 * ``errorElement`` on every route, and the root boundary around the
 * provider tree. The card follows Flow M Frame 4's failure-card register
 * (heading, body, actions, a ``<details>`` diagnostic disclosure) and
 * REQ-UI-037's no-leakage rule: the surface is named in operator words,
 * the stack is never rendered, and the error's name + message sit behind a
 * disclosure that is closed by default. The stack rides a ``data-debug``
 * attribute — the same channel :func:`session-provider.BootstrapError`
 * uses — so browser-extension diagnostics can read it without it ever
 * being painted.
 *
 * Why a card and not a toast: a render crash is persistent, not transient,
 * and the surface it replaced is gone until the operator acts. Flow M's
 * "Notifications and toasts" note draws the same line for Frame 4.
 *
 * Cross-references:
 *   - docs/engineering/detail/storyboards/flows/M-empty-states (Frames 4 + 5).
 *   - docs/engineering/detail/storyboards/shared/design-system.md
 *     ("Failure card" component pattern).
 *   - REQ-UI-191 (render-error containment); REQ-UI-037 (no leakage).
 *   - CLAUDE.md §8.1 (operator visibility; fail-safe).
 */
import { describeError } from "@/chrome/describe-error";
import { cn } from "@/lib/utils";

/** Where the boundary sits — it decides the copy and which actions make sense. */
export type ErrorFallbackScope = "panel" | "canvas" | "page";

/** A button (``onClick``) or a plain link (``href``) the card offers. */
export type ErrorFallbackAction =
  | { label: string; onClick: () => void; href?: undefined }
  | { label: string; href: string; onClick?: undefined };

export interface ErrorFallbackProps {
  /** Decides the copy: ``panel`` and ``canvas`` leave the rest of the app alive; ``page`` does not. */
  scope: ErrorFallbackScope;
  /**
   * The failed surface in operator words — a panel title (``"Map"``), a
   * route (``"workspace"``, ``"sign-in page"``), or ``"Meridian"`` at page
   * scope. Rendered as "The {surface} panel stopped working." for panels and
   * "The {surface} stopped working." otherwise; ``"Meridian"`` is rendered
   * bare.
   */
  surface: string;
  /** Whatever was thrown. Usually an :class:`Error`; anything is tolerated. */
  error: unknown;
  /** The one action the operator should reach for first. */
  primaryAction: ErrorFallbackAction;
  /** An optional lesser action — "Reload page" under a panel-level retry. */
  secondaryAction?: ErrorFallbackAction;
  /** Optional ``data-testid`` for the card root. */
  testId?: string;
}

function headingFor(scope: ErrorFallbackScope, surface: string): string {
  if (scope === "panel") {
    return `The ${surface} panel stopped working.`;
  }
  if (surface === "Meridian") {
    return "Meridian stopped working.";
  }
  return `The ${surface} stopped working.`;
}

function bodyFor(scope: ErrorFallbackScope): string {
  switch (scope) {
    case "panel":
      return (
        "Something went wrong while drawing this panel. The rest of Meridian is unaffected " +
        "and your live data is still arriving. Reload the panel to try again; if it keeps " +
        "failing, reload the page or contact support with the diagnostic details."
      );
    case "canvas":
      return (
        "Something went wrong while drawing this part of the application. The rest of Meridian " +
        "is unaffected and you are still signed in. Reload the page to try again; if it keeps " +
        "failing, contact support with the diagnostic details."
      );
    case "page":
      return (
        "Something went wrong and the application could not continue. Your sign-in is " +
        "preserved. Reload the page to recover; if it keeps failing, contact support with the " +
        "diagnostic details."
      );
  }
}

const PRIMARY_CLASS =
  "rounded bg-brand px-3 py-1.5 text-body-sm font-medium text-bg-base hover:bg-brand-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const SECONDARY_CLASS =
  "rounded border border-border px-3 py-1.5 text-body-sm text-text-secondary hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function ActionControl({
  action,
  className,
}: {
  action: ErrorFallbackAction;
  className: string;
}): React.ReactElement {
  if (action.href !== undefined) {
    return (
      <a href={action.href} className={cn("inline-block", className)}>
        {action.label}
      </a>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={className}>
      {action.label}
    </button>
  );
}

/**
 * Render the failure card.
 *
 * ``role="alert"`` so assistive tech announces the replacement the moment
 * it appears (Flow M "Accessibility"). The disclosure is a native
 * ``<details>`` for screen-reader-friendly progressive disclosure, as
 * Frame 4 specifies.
 */
export function ErrorFallback({
  scope,
  surface,
  error,
  primaryAction,
  secondaryAction,
  testId,
}: ErrorFallbackProps): React.ReactElement {
  const described = describeError(error);
  const occurredAt = new Date().toISOString();
  return (
    <div
      role="alert"
      data-testid={testId ?? "error-fallback"}
      data-debug={described.debug}
      className={cn(
        "flex h-full w-full items-center justify-center overflow-auto bg-bg-base p-4 text-text-primary",
      )}
    >
      <div className="w-full max-w-[480px] rounded-md border border-destructive/40 bg-bg-elevated p-5 shadow-md">
        <h2 className="text-h2 text-text-primary">{headingFor(scope, surface)}</h2>
        <p className="mt-2 text-body text-text-secondary">{bodyFor(scope)}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <ActionControl action={primaryAction} className={PRIMARY_CLASS} />
          {secondaryAction ? (
            <ActionControl action={secondaryAction} className={SECONDARY_CLASS} />
          ) : null}
        </div>
        <details className="mt-4 text-body-sm text-text-muted" data-testid="error-fallback-details">
          <summary className="cursor-pointer select-none text-text-secondary">
            Show diagnostic details
          </summary>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt>Surface</dt>
            <dd className="font-mono text-code">{surface}</dd>
            <dt>Time</dt>
            <dd className="font-mono text-code">{occurredAt}</dd>
            <dt>Error</dt>
            <dd className="break-words font-mono text-code">{described.summary}</dd>
          </dl>
        </details>
      </div>
    </div>
  );
}
